import React, { useState } from 'react';
import {
  Button,
  TextField,
  Container,
  Typography,
  Box,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Divider,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import LockIcon from '@mui/icons-material/Lock';
import PublicOffIcon from '@mui/icons-material/PublicOff';
import './login.css'; // Importa estilos personalizados (si aplica)

function Login({ onLogin }) {
  // Estado para manejar el input del usuario y contraseña
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  // Función para manejar el envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(
        process.env.REACT_APP_API_URL + '/auth/login',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, password }),
        }
      );

      if (response.ok) {
        const data = await response.json();
      // console.log('Token received:', data.token); // Log para verificar el token recibido
        localStorage.setItem('token', data.token); // Guardar el token en localStorage
        onLogin(data.token);
      } else {
        alert('Invalid credentials. Please try again.');
      }
    } catch (error) {
      console.error('Error during login:', error);
      alert('An unexpected error occurred. Please try again later.');
    }
  };

  const handleLoginClick = () => setShowLoginForm(true);
  const handleClickShowPassword = () => setShowPassword(!showPassword);
  const handleMouseDownPassword = (event) => event.preventDefault();

  // Pantalla inicial restringida
  if (!showLoginForm) {
    return (
      <Box
        className="main-container"
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(to right, rgba(0, 0, 255, 0.18), rgba(0, 128, 0, 0.15))', // Fondo azulado degradado
          color: '#ffffff',
        }}
      >
        <Card
          sx={{
            maxWidth: 500,
            borderRadius: 4,
            boxShadow: 6,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <LockIcon sx={{ fontSize: 70, color: '#3f51b5' }} />
            </Box>

            <Typography component="h1" variant="h4" align="center" gutterBottom>
              Access Restricted
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography align="center" color="text.terciary" sx={{ mb: 3 }}>
            Authentication is required to access the analysis and evaluate the accuracy of our algorithm against ground truth and cross-border benchmarks.
            </Typography>
            <Typography align="center" color="text.secondary" sx={{ mb: 2 }}>
            Please log in with your credentials
            </Typography>
     

            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                sx={{ px: 4, borderRadius: 2, fontWeight: 'bold' }}
                onClick={handleLoginClick}
              >
                Proceed to Login
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Formulario de inicio de sesión
  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: '100vh',       // Altura completa de la pantalla
        width: '100%',            // Ancho completo del contenedor
        maxWidth: '100% !important', // Sobrescribir cualquier restricción de Material-UI
        margin: 0,                // Eliminar márgenes
        padding: 0,               // Eliminar padding
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: 'linear-gradient(to right, rgba(0, 0, 255, 0.18), rgba(0, 128, 0, 0.15))',
      }}
    >
      <Box
        sx={{
          marginTop: -12,
          width: '25%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          backgroundColor: '#ffffff',
          borderRadius: 4,
          boxShadow: 3,
          p: 3,
        }}
      >
        <AccountCircleOutlinedIcon sx={{ fontSize: 80, color: '#4facfe' }} />
        <Typography component="h1" variant="h5" sx={{ mt: 1, mb: 2 }}>
          Welcome!
        </Typography>
  
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="username"
            label="Username"
            name="username"
            autoComplete="username"
            autoFocus
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            InputProps={{
              style: { borderRadius: 8 },
            }}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            id="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
              style: { borderRadius: 8 },
            }}
          />
  
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              mt: 3,
              mb: 2,
              backgroundColor: '#283593',
              '&:hover': { backgroundColor: '#1a237e' },
              borderRadius: 2,
              fontWeight: 'bold',
            }}
          >
            Sign In
          </Button>
          <div className="contact-container">
            <Typography variant="body2" align="center" sx={{ mt: 0 }}>
              Don’t have credentials?
              <br />
              <Link
                href="mailto:lcirujan@pa.uc3m.es?subject=Request%20for%20Access%20to%20the%20Map&body=Hello,%0D%0A%0D%0AI%20would%20like%20to%20request%20credentials%20to%20access%20the%20restricted%20map%20section.%0D%0APlease%20let%20me%20know%20how%20I%20can%20proceed.%0D%0A%0D%0AThank%20you%20in%20advance!%0D%0ABest%20regards,%0D%0A[Your%20Name]"
                underline="hover"
                sx={{ textDecoration: 'underline' }}
              >
                Contact support
              </Link>
            </Typography>
          </div>
        </Box>
      </Box>
    </Container>
  );
}

export default Login;
